// Questionnaire santé (#/sante, 14/10). Session-local DEMO shared store, no backend, no real health input.
// - DEFAULT: no health gate anywhere (config.required = false), every validated flow unchanged.
// - The gate exists only through an EXPLICIT configuration: #/sante?etat=attente|valide|echange activates the DEMO configuration
//   (required + requireTraining + requireBooking) with the DEMO snapshot below, so a direct reload of that URL gives the same
//   state. The status is NEVER derived from the answers (no auto « Oui » → échange, needsTalk is not used here): it is the coach's
//   review, or « attente » when nothing has been reviewed yet.
// - A LOCAL (demo) submission of the new Bilan initial replaces the snapshot with the member's OWN answers (immutable copy taken at
//   submission, status « attente »), and only while the configuration is active: nothing is fabricated as submitted.
// - Gate (pure, tested): training / booking blocked while required and not reviewed as « valide ». History stays readable.
import { useSyncExternalStore } from "react";
import { HEALTH_QUESTIONS, type Bilan, type HealthAnswer } from "./bilan-state";
import { COACHES } from "./messagerie-state";
import { DEMO_NOW } from "./demo-context";
import { account } from "./compte-state";

import { healthGate, purgeOwnAnswers, snapshotOf, visibleAnswers, type HealthConfig, type HealthReview, type HealthSnapshot, type HealthState, type HealthStatus } from "./health-model";
export { healthGate, visibleAnswers, type HealthStatus, type HealthSnapshot, type HealthState };

export const ANSWER_LABEL: Record<HealthAnswer, string> = { oui: "Oui", non: "Non", parler: "Je préfère en parler directement" };
export const STATUS_LABEL: Record<HealthStatus, string> = { attente: "En attente de validation", valide: "Validation reçue", echange: "Échange demandé" };

const NO_CONFIG: HealthConfig = { required: false, requireTraining: false, requireBooking: false };
const DEMO_CONFIG: HealthConfig = { required: true, requireTraining: true, requireBooking: true };

/** DEMO answers (fictional, not an ACS record nor a clinical statement), sent Saturday 10 October 18:05, before the demo clock. */
const demoAnswer: Record<string, { a: HealthAnswer; detail?: string }> = {
  douleurs: { a: "oui", detail: "Gêne au genou droit dans les escaliers (réponse de démonstration)." },
  operation: { a: "non" }, probleme: { a: "non" }, traitement: { a: "non" }, restriction: { a: "non" }, suivi: { a: "non" },
};
export const DEMO_SNAPSHOT: HealthSnapshot = Object.freeze({
  source: "demo", submittedAt: "2026-10-10T18:05",
  answers: Object.freeze(HEALTH_QUESTIONS.map(x => Object.freeze({ id: x.id, q: x.q, ...demoAnswer[x.id]! }))),
});
const coach = COACHES[0]?.name;
/** DEMO reviews, all at or before the demo clock (Monday 12 October 16:00). */
const DEMO_REVIEW: Record<HealthStatus, HealthReview> = {
  attente: { status: "attente" },
  valide: { status: "valide", coach, reviewedAt: "2026-10-12T09:40" },
  echange: { status: "echange", coach, reviewedAt: "2026-10-12T09:40", comment: "J’aimerais qu’on en parle ensemble avant ta prochaine séance. (message de démonstration)" },
};

export const ETAT_RE = /etat=(attente|valide|echange)/;
/** Explicit configuration from the URL of the health route only. */
export function stateFromHash(hash: string): HealthState | null {
  if (!/^#\/?sante\b/.test(hash)) return null;
  const etat = hash.match(ETAT_RE)?.[1] as HealthStatus | undefined;
  return etat ? { config: DEMO_CONFIG, snapshot: DEMO_SNAPSHOT, review: DEMO_REVIEW[etat] } : null;
}

let state: HealthState = stateFromHash(typeof window === "undefined" ? "" : window.location.hash) ?? { config: NO_CONFIG, snapshot: null, review: { status: "attente" } };
const listeners = new Set<() => void>();
const emit = (next: HealthState) => { state = next; listeners.forEach(l => l()); };

export const health = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  /** Applies the explicit URL configuration (same route, new query). */
  // The member's OWN snapshot (source « bilan », purged or not, from the local bilan submission) is never replaced by the DEMO
  // fixture nor re-reviewed from an old ?etat= in the URL (back navigation, context link).
  syncFromHash: (hash: string) => { if (state.snapshot?.source === "bilan") return; const s = stateFromHash(hash); if (s && (s.review.status !== state.review.status || state.snapshot?.source !== "demo" || !state.config.required)) emit(s); },
  /** Local submission of the new bilan (demo, nothing is sent): own snapshot, « attente », only when the configuration is active;
   *  answers kept only with the health-data consent (compte-state) and the « questions » choice. */
  submitBilan: (b: Bilan) => { if (state.config.required) emit({ ...state, snapshot: snapshotOf(b, DEMO_NOW, HEALTH_QUESTIONS, account.get().consents.sensibles), review: { status: "attente" } }); },
};
// Same pattern as bilan-state (syncConsents): withdrawing the health-data consent purges the member's own answers in memory.
account.subscribe(() => { if (!account.get().consents.sensibles) { const next = purgeOwnAnswers(state.snapshot); if (next !== state.snapshot) emit({ ...state, snapshot: next }); } });
export const useHealth = () => useSyncExternalStore(health.subscribe, health.get, health.get);
export const isConfigured = (s: HealthState) => s.config.required && !!s.snapshot;
